import Strings from "utils/strings"
import { SectorsMapDraft } from "./types"

export const XLSX_MIME =
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

export const downloadBytes = (
	bytes: Uint8Array,
	fileName: string,
	mimeType: string
) => {
	const blob = new Blob([bytes], { type: mimeType })
	const link = document.createElement("a")
	const url = URL.createObjectURL(blob)
	link.href = url
	link.download = fileName
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

// Error codes come straight from dingoo-api's order import pipeline
export const summarizeFailures = (failedOrders: any[]) => {
	const failureReasonLabels: Record<string, string> = {
		order_id: Strings.sectorsMap.failureReasonOrderId,
		duplicate_order_id: Strings.sectorsMap.failureReasonDuplicateOrderId,
		missing_fields: Strings.sectorsMap.failureReasonMissingFields,
		duplicate_key: Strings.sectorsMap.failureReasonDuplicateKey,
		database_validation_failed:
			Strings.sectorsMap.failureReasonDatabaseValidationFailed,
		unknown_database_error:
			Strings.sectorsMap.failureReasonUnknownDatabaseError,
	}
	const groups: Record<string, any[]> = {}
	failedOrders.forEach((f) => {
		const label =
			failureReasonLabels[f.error] ||
			f.error ||
			Strings.sectorsMap.failureReasonUnknown
		groups[label] = groups[label] || []
		groups[label].push(f)
	})
	return Object.entries(groups)
}

export const buildSectorsFile = async (orders: any[]) => {
	const XLSX = await import("xlsx")
	const sheet = XLSX.utils.json_to_sheet(
		orders.map((o) => ({ ...o, Lot: o.sector }))
	)
	const book = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(book, sheet, "Sheet1")
	const bytes = XLSX.write(book, { bookType: "xlsx", type: "array" })
	return new Blob([bytes], { type: XLSX_MIME })
}

// Survives an accidental refresh/tab close, cleared only on "Novo Ficheiro".
// generatedFile is left out on purpose: it's binary and re-derivable.
const DRAFT_STORAGE_KEY = "dingoo-bo-sectors-map-draft"

// Only same-day drafts are restored — the next day it's almost certainly a
// different file, and reappearing with yesterday's data would just confuse.
const isSameDay = (a: number, b: number) =>
	new Date(a).toDateString() === new Date(b).toDateString()

export const loadDraft = (): SectorsMapDraft | null => {
	try {
		const raw = localStorage.getItem(DRAFT_STORAGE_KEY)
		if (!raw) return null

		const draft: SectorsMapDraft = JSON.parse(raw)
		if (!isSameDay(draft.savedAt, Date.now())) {
			clearDraft()
			return null
		}
		return draft
	} catch {
		return null
	}
}

export const saveDraft = (draft: Omit<SectorsMapDraft, "savedAt">) => {
	try {
		const withTimestamp: SectorsMapDraft = { ...draft, savedAt: Date.now() }
		localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(withTimestamp))
	} catch {
		// Storage full/unavailable: losing the draft cache is a soft failure.
	}
}

export const clearDraft = () => {
	localStorage.removeItem(DRAFT_STORAGE_KEY)
}
